/**
 * SmartOdometerCapture — Reusable 3-Tier Odometer Capture Widget
 *
 * Drop-in component that handles the full odometer capture flow:
 *  Tier 1 → Auto-fetch from CarTrack / GPS tracking
 *  Tier 2 → Photo fallback with AI verification
 *  Tier 3 → Manual numeric entry (always available as fallback)
 *
 * Connects to useOdometerCapture hook internally, or accepts an external
 * hook instance for parent coordination.
 */

'use client';

import React, { useCallback, useState } from 'react';
import {
  Gauge, Signal, Camera, Loader2, CheckCircle2,
  RefreshCw, Sparkles, ImageIcon, ShieldCheck,
} from 'lucide-react';
import { useCamera, SimpleCameraModal } from '@/hooks/useCamera';
import type { UseOdometerCaptureReturn } from '@/hooks/useOdometerCapture';

// ============================================================================
// PROPS
// ============================================================================

interface SmartOdometerCaptureProps {
  /** Pre-initialised hook instance from the parent */
  capture: UseOdometerCaptureReturn;
  /** Label shown above the widget (defaults to "Odometer Reading (km)") */
  label?: string;
  /** Mark as required with red asterisk */
  required?: boolean;
  /** Disable interaction */
  disabled?: boolean;
  /** Accent colour for badges */
  accentColor?: string;
  /** Compact mode — smaller height, no photo preview */
  compact?: boolean;
  /** Legacy compatibility flag. SmartOdometerCapture now always uses the JS/in-app camera path. */
  preferNativeCameraOnCapacitor?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function SmartOdometerCapture({
  capture,
  label = 'Odometer Reading (km)',
  required = false,
  disabled = false,
  accentColor = '#3b82f6',
  compact = false,
  preferNativeCameraOnCapacitor = false,
}: SmartOdometerCaptureProps) {
  const {
    status, source, reading, isTracked, trackingProvider, readingIsLive, photo,
    verification, statusMessage, loading, error, isComplete, allowManualEntry,
    fetchFromTracking, submitPhoto, setManualReading, updateManualReading,
  } = capture;

  // Camera for photo capture
  const [showCamera, setShowCamera] = useState(false);
  const camera = useCamera({
    initialFacingMode: 'environment',
    preferNativeCameraOnCapacitor,
    quality: 0.9,
    onCapture: (base64: string) => {
      submitPhoto(base64);
    },
  });

  const handleTakePhoto = useCallback(() => {
    camera.open();
  }, [camera]);

  // Manual input state
  const [manualInput, setManualInput] = useState('');

  const handleManualSubmit = useCallback(() => {
    const val = parseInt(manualInput, 10);
    if (!isNaN(val) && val > 0) {
      setManualReading(val);
    }
  }, [manualInput, setManualReading]);

  // ── Success: Tracking ──────────────────────────────────────────
  if (status === 'tracking_success' && reading != null) {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="w-full px-3 py-2.5 rounded-xl border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
              {reading.toLocaleString()} km
            </span>
          </div>
          <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600 dark:text-green-400">
            <Signal className="w-3 h-3" />
            {readingIsLive ? 'Live' : 'Synced'} · {trackingProvider || 'GPS'}
          </span>
        </div>
      </div>
    );
  }

  // ── Success: Photo Verified ────────────────────────────────────
  if (status === 'photo_verified' && reading != null) {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="w-full px-3 py-2.5 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                {reading.toLocaleString()} km
              </span>
            </div>
            <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400">
              <Sparkles className="w-3 h-3" />
              AI Verified
            </span>
          </div>
          {verification && (
            <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1">
              Confidence: {Math.round(verification.confidence * 100)}%
              {verification.validation === 'mismatch' && (
                <span className="text-amber-600 ml-2">⚠ {verification.validation_message}</span>
              )}
            </p>
          )}
          {/* Small photo thumbnail */}
          {photo && !compact && (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={photo}
                alt="Odometer"
                className="w-16 h-12 object-cover rounded-lg border border-blue-200 dark:border-blue-700"
              />
              <button
                onClick={handleTakePhoto}
                disabled={disabled}
                className="text-[10px] text-blue-600 hover:text-blue-700 underline"
              >
                Retake photo
              </button>
            </div>
          )}
        </div>
        <SimpleCameraModal camera={camera} title="Odometer Photo" />
      </div>
    );
  }

  // ── Success: Photo captured offline, verification pending ─────────
  if (status === 'complete' && reading != null && source === 'photo_pending_verification') {
    const usedApiFallback = verification?.validation === 'api_error_fallback';
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="w-full px-3 py-2.5 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ImageIcon className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                {reading.toLocaleString()} km
              </span>
            </div>
            <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400 text-right">
              <Sparkles className="w-3 h-3" />
              {usedApiFallback ? 'API Fallback' : 'AI Pending'}
            </span>
          </div>
          <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
            {verification?.validation_message || 'Photo saved offline. AI will verify this reading when sync resumes.'}
          </p>
          {photo && !compact && (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={photo}
                alt="Pending odometer verification"
                className="w-16 h-12 object-cover rounded-lg border border-amber-200 dark:border-amber-700"
              />
              <button
                onClick={handleTakePhoto}
                disabled={disabled}
                className="text-[10px] text-amber-700 hover:text-amber-800 underline"
              >
                Retake photo
              </button>
            </div>
          )}
        </div>
        <SimpleCameraModal camera={camera} title="Odometer Photo" />
      </div>
    );
  }

  // ── Complete: Manual entry ─────────────────────────────────────
  if (status === 'complete' && reading != null && source === 'manual') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
              {reading.toLocaleString()} km
            </span>
          </div>
          <span className="text-[10px] text-gray-500">Manual entry</span>
        </div>
      </div>
    );
  }

  // ── Loading: Fetching from tracker ─────────────────────────────
  if (status === 'fetching_tracking') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="w-full px-3 py-3 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-sm text-blue-600 dark:text-blue-400">Fetching from vehicle tracker…</span>
        </div>
      </div>
    );
  }

  // ── Loading: Verifying photo ───────────────────────────────────
  if (status === 'verifying_photo') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="w-full px-3 py-3 rounded-xl border border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
            <div>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">AI Verifying Odometer…</span>
              <p className="text-[10px] text-purple-600 dark:text-purple-400 mt-0.5">Reading the odometer digits</p>
            </div>
          </div>
          {photo && !compact && (
            <img
              src={photo}
              alt="Verifying..."
              className="mt-2 w-full h-20 object-cover rounded-lg border border-purple-200 dark:border-purple-700 opacity-60"
            />
          )}
        </div>
        <SimpleCameraModal camera={camera} title="Odometer Photo" />
      </div>
    );
  }

  // ── Tracking Failed / Photo Required ───────────────────────────
  if (status === 'tracking_failed' || status === 'photo_rejected' || status === 'awaiting_photo') {
    const isRejected = status === 'photo_rejected';
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {/* Photo capture area */}
        {photo && !compact ? (
          <div className="relative mb-2">
            <img
              src={photo}
              alt="Rejected odometer"
              className="w-full h-32 object-cover rounded-xl border-2 border-red-300 dark:border-red-600 opacity-50"
            />
            <button
              onClick={handleTakePhoto}
              disabled={disabled || loading}
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/30 rounded-xl text-white hover:bg-black/40 transition-colors"
            >
              <Camera className="w-6 h-6" />
              <span className="text-xs font-medium">Retake Photo</span>
            </button>
          </div>
        ) : (
          <button
            onClick={handleTakePhoto}
            disabled={disabled || loading}
            className="w-full py-3 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-600 flex flex-col items-center justify-center gap-1.5 text-blue-600 dark:text-blue-400 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <Camera className="w-6 h-6" />
            <span className="text-xs font-semibold">Take Odometer Photo</span>
            <span className="text-[10px] text-blue-500 dark:text-blue-500 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI will verify the reading
            </span>
          </button>
        )}

        {isRejected && error && (
          <p className="text-[10px] text-red-600 dark:text-red-400 mt-1.5">{error}</p>
        )}

        {isTracked && (
          <button
            onClick={fetchFromTracking}
            disabled={disabled}
            className="mt-2 text-[10px] text-amber-600 hover:text-amber-700 dark:text-amber-400 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Retry tracker
          </button>
        )}

        <SimpleCameraModal camera={camera} title="Odometer Photo" />
      </div>
    );
  }

  // ── Photo saved offline, reading still needed ─────────────────────
  if (status === 'photo_pending_sync') {
    const usedApiFallback = verification?.validation === 'api_error_fallback';
    return (
      <div>
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        <div className="w-full px-3 py-2.5 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 mb-2">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <div>
              <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                {usedApiFallback ? 'Verification service unavailable' : 'Photo saved offline'}
              </span>
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                {usedApiFallback
                  ? 'Verification is temporarily unavailable. Enter the visible reading now so you can continue.'
                  : 'Enter the visible reading now. AI will verify the photo automatically after sync.'}
              </p>
            </div>
          </div>
        </div>

        {photo && !compact && (
          <div className="relative mb-2">
            <img
              src={photo}
              alt="Pending odometer verification"
              className="w-full h-32 object-cover rounded-xl border-2 border-amber-300 dark:border-amber-600"
            />
            <button
              onClick={handleTakePhoto}
              disabled={disabled || loading}
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/25 rounded-xl text-white hover:bg-black/35 transition-colors"
            >
              <Camera className="w-6 h-6" />
              <span className="text-xs font-medium">Retake Photo</span>
            </button>
          </div>
        )}

        <input
          type="number"
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={manualInput || (reading ? String(reading) : '')}
          onChange={(e) => {
            const v = e.target.value;
            setManualInput(v);
            // Propagate live so form gating reacts immediately — the
            // draft update keeps status unchanged so this input stays
            // mounted while typing.
            const val = parseInt(v, 10);
            updateManualReading(!isNaN(val) && val > 0 ? val : null);
          }}
          onBlur={() => {
            const val = parseInt(manualInput, 10);
            if (!isNaN(val) && val > 0) {
              setManualReading(val);
            }
          }}
          placeholder="Enter visible odometer reading"
          disabled={disabled}
          className="w-full px-3 py-2.5 bg-white dark:bg-gray-700 rounded-xl border border-amber-200 dark:border-amber-700 text-sm font-mono text-center focus:ring-2 focus:ring-amber-500 focus:outline-none disabled:opacity-50"
        />

        <SimpleCameraModal camera={camera} title="Odometer Photo" />
      </div>
    );
  }

  // ── Manual Entry (untracked vehicle) ───────────────────────────
  // Photo is shown as the preferred path (with AI verification badge),
  // but manual numeric entry is always available as a fallback.
  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {allowManualEntry && status === 'manual_entry' && (
        <div className="space-y-2">
          {isTracked && error && (
            <div className="w-full px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-[10px] text-amber-700 dark:text-amber-300">
              {error}
            </div>
          )}
          {/* Preferred: photo capture with AI verification */}
          <button
            onClick={handleTakePhoto}
            disabled={disabled || loading}
            className="w-full py-3 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-600 flex flex-col items-center justify-center gap-1.5 text-blue-600 dark:text-blue-400 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <Camera className="w-6 h-6" />
            <span className="text-xs font-semibold">Take Odometer Photo</span>
            <span className="text-[10px] text-blue-500 dark:text-blue-500 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI will verify the reading
            </span>
          </button>
          {/* Manual entry fallback */}
          <div className="relative flex items-center gap-2">
            <div className="flex-1 border-t border-gray-200 dark:border-gray-600" />
            <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">or enter manually</span>
            <div className="flex-1 border-t border-gray-200 dark:border-gray-600" />
          </div>
          <input
            type="number"
            inputMode="numeric"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            value={manualInput || (reading ? String(reading) : '')}
            onChange={(e) => {
              const v = e.target.value;
              setManualInput(v);
              // Propagate live so form gating reacts immediately — the
              // draft update keeps status unchanged so this input stays
              // mounted while typing.
              const val = parseInt(v, 10);
              updateManualReading(!isNaN(val) && val > 0 ? val : null);
            }}
            onBlur={() => {
              const val = parseInt(manualInput, 10);
              if (!isNaN(val) && val > 0) {
                setManualReading(val);
              }
            }}
            placeholder="e.g. 145230"
            disabled={disabled}
            className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-mono text-center focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
          />
          {isTracked && (
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Manual entry for tracked vehicle — photo recommended for audit trail
              </p>
              <button
                onClick={fetchFromTracking}
                disabled={disabled}
                className="text-[10px] text-amber-600 hover:text-amber-700 dark:text-amber-400 flex items-center gap-1 shrink-0"
              >
                <RefreshCw className="w-3 h-3" /> Retry tracker
              </button>
            </div>
          )}
        </div>
      )}

      {/* Idle state — should not normally be seen */}
      {status === 'idle' && (
        <div className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-400 text-center">
          Preparing…
        </div>
      )}

      <SimpleCameraModal camera={camera} title="Odometer Photo" />
    </div>
  );
}
